/**
 * Dialog360 Billing Tab
 * Real WhatsApp spend pulled from 360dialog (Meta's WABA conversation analytics,
 * proxied by waba-v2 — 360dialog adds no markup over Meta's rate cards). Shows total
 * spend, a per-category breakdown, and a daily trend over a selectable window.
 */

import { useEffect, useState } from 'react';
import { IconRefresh, IconReceipt2, IconCoin, IconMessages, IconInfoCircle } from '@tabler/icons-react';
import {
  Stack,
  Group,
  Button,
  Text,
  Title,
  Badge,
  Card,
  Alert,
  Loader,
  Divider,
  SimpleGrid,
  ScrollArea,
  Table,
  SegmentedControl,
  Progress,
} from '../../../../../components/ui';
import { useDialog360 } from '../hooks/useDialog360';
import { conversationCategoryMeta } from '../dialog360.types';

function money(v: number, currency?: string | null): string {
  const cur = currency || 'USD';
  try {
    return new Intl.NumberFormat(undefined, { style: 'currency', currency: cur }).format(v);
  } catch {
    return `${cur} ${v.toFixed(2)}`;
  }
}

const PERIODS = [
  { label: '7 days', value: '7' },
  { label: '30 days', value: '30' },
  { label: '90 days', value: '90' },
];

function Kpi({ icon, label, value, sub }: { icon: React.ReactNode; label: string; value: string; sub?: string }) {
  return (
    <Card withBorder padding="md">
      <Group gap="sm" align="flex-start" wrap="nowrap">
        <div style={{ opacity: 0.6 }}>{icon}</div>
        <Stack gap={2}>
          <Text size="xs" c="dimmed" tt="uppercase">{label}</Text>
          <Title order={3}>{value}</Title>
          {sub && <Text size="xs" c="dimmed">{sub}</Text>}
        </Stack>
      </Group>
    </Card>
  );
}

export function Dialog360BillingTab() {
  const { loading, error, billing, fetchBilling } = useDialog360();
  const [days, setDays] = useState('30');

  useEffect(() => {
    void fetchBilling(Number(days));
  }, [fetchBilling, days]);

  const currency = billing?.currency;
  const totalCost = billing?.totalCost ?? 0;
  const totalConv = billing?.totalConversations ?? 0;
  const avg = totalConv > 0 ? totalCost / totalConv : 0;
  const maxTrendCost = Math.max(0, ...(billing?.trend ?? []).map((t) => t.cost));
  const hasData = !!billing && totalConv > 0;

  return (
    <Stack gap="md">
      <Card>
        <Group justify="space-between" align="flex-start" wrap="wrap">
          <div>
            <Text fw={500} size="lg">WhatsApp Billing</Text>
            <Text size="sm" c="dimmed">
              Real conversation spend from 360dialog (Meta rate cards — 360dialog adds no markup).
            </Text>
          </div>
          <Group gap="sm">
            <SegmentedControl size="xs" value={days} onChange={setDays} data={PERIODS} />
            <Button variant="light" size="xs" leftSection={<IconRefresh size={14} />} onClick={() => fetchBilling(Number(days))} loading={loading}>
              Refresh
            </Button>
          </Group>
        </Group>
      </Card>

      {error && (
        <Alert color="red" icon={<IconInfoCircle size={16} />} title="Billing data unavailable">
          {error}
        </Alert>
      )}

      {loading && !billing ? (
        <Group justify="center" p="xl"><Loader size="lg" /></Group>
      ) : !error && billing && !hasData ? (
        <Card withBorder padding="xl">
          <Text size="sm" c="dimmed">No WhatsApp conversations in this period ({billing.start} → {billing.end}).</Text>
        </Card>
      ) : hasData ? (
        <>
          <SimpleGrid cols={{ base: 1, sm: 3 }} spacing="md">
            <Kpi icon={<IconCoin size={22} />} label="Total spend" value={money(totalCost, currency)} sub={`${billing!.start} → ${billing!.end}`} />
            <Kpi icon={<IconMessages size={22} />} label="Conversations" value={totalConv.toLocaleString()} />
            <Kpi icon={<IconReceipt2 size={22} />} label="Avg / conversation" value={money(avg, currency)} />
          </SimpleGrid>

          <SimpleGrid cols={{ base: 1, md: 2 }} spacing="md">
            {/* Spend by category */}
            <Card withBorder padding="md">
              <Text fw={500} size="sm" mb="xs">Spend by category</Text>
              <Divider mb="sm" />
              <Stack gap="sm">
                {billing!.categories.map((c) => {
                  const meta = conversationCategoryMeta(c.category);
                  const share = totalCost > 0 ? (c.cost / totalCost) * 100 : 0;
                  return (
                    <div key={c.category}>
                      <Group justify="space-between" mb={4} wrap="nowrap">
                        <Group gap="xs" wrap="nowrap">
                          <Badge size="sm" variant="light" color={meta.color}>{meta.label}</Badge>
                          <Text size="xs" c="dimmed">{c.conversations.toLocaleString()} convos</Text>
                        </Group>
                        <Text size="sm" fw={500}>{money(c.cost, currency)}</Text>
                      </Group>
                      <Progress value={share} gradientVariant={meta.gradientVariant} size="sm" radius="sm" />
                    </div>
                  );
                })}
              </Stack>
            </Card>

            {/* Daily trend bar chart */}
            <Card withBorder padding="md">
              <Text fw={500} size="sm" mb="xs">Daily spend</Text>
              <Divider mb="sm" />
              {billing!.trend.length === 0 ? (
                <Text size="xs" c="dimmed">No daily data.</Text>
              ) : (
                <Group gap={3} align="flex-end" wrap="nowrap" style={{ height: 140, overflowX: 'auto' }}>
                  {billing!.trend.map((t) => {
                    const h = maxTrendCost > 0 ? Math.max(2, (t.cost / maxTrendCost) * 130) : 2;
                    return (
                      <div
                        key={t.date}
                        title={`${t.date}: ${money(t.cost, currency)} · ${t.conversations} convos`}
                        style={{ flex: '1 0 6px', minWidth: 6, height: h, background: 'var(--mantine-color-teal-5)', borderRadius: 2 }}
                      />
                    );
                  })}
                </Group>
              )}
              <Text size="9px" c="dimmed" mt="xs">Hover a bar for the day's spend.</Text>
            </Card>
          </SimpleGrid>

          {/* Daily breakdown table */}
          <Card withBorder padding="sm">
            <Text fw={500} size="sm" mb="xs">Daily breakdown</Text>
            <Divider mb="xs" />
            <ScrollArea h={320}>
              <Table stickyHeader fz="xs" verticalSpacing={6} variant="hoverable">
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Date</Table.Th>
                    <Table.Th ta="right">Conversations</Table.Th>
                    <Table.Th ta="right">Spend</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {[...billing!.trend].reverse().map((t) => (
                    <Table.Tr key={t.date}>
                      <Table.Td>{t.date}</Table.Td>
                      <Table.Td ta="right">{t.conversations.toLocaleString()}</Table.Td>
                      <Table.Td ta="right">{money(t.cost, currency)}</Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            </ScrollArea>
          </Card>

          <Text size="xs" c="dimmed">
            Figures come directly from 360dialog (Meta conversation analytics). Service-category and free-tier
            conversations may show {money(0, currency)}. Currency: {currency || 'USD'}.
          </Text>
        </>
      ) : null}
    </Stack>
  );
}
